import React from "react";
import { Link, withRouter } from "react-router-dom";

import { IAppPropsWithRoute } from "../../../common/types";
import { makeStyles, MuiThemeProvider } from "@material-ui/core/styles";
import { Paper, Grid } from "@material-ui/core";
import themeDark from "../../shared/themeDark";
import CalloutRequestedTop from "./CalloutRequestedTop";
import CalloutRequestedStepper from "./CalloutRequestedStepper";
import CalloutRequestedTopIcon from "./CalloutRequestedTopIcon";
import { createCalloutSteps } from "./calloutRequestedData";
import api from "../../../store/api";
import ErrorMessage from "../../shared/messages/ErrorMessage";
import RequestCalloutTypes from "../types/RequestCalloutTypes";
import { IRequestCalloutDTO } from "./types/IRequestCalloutDTOs";
import { adjustStatusToActiveStepId, prepareSteps } from "./calloutRequestedHelpers";
import Dispatched from "./stepWidgets/Dispatched";
import Feedback from "./stepWidgets/Feedback";
import Acknowledged from "./stepWidgets/Acknowledged";
import TeamOnTheirWay from "./stepWidgets/TeamOnTheirWay";
import { addTaskForEvent, removeTaskForEvent } from "../../../store/signalr/tasksForEvents";
import eventNames from "../../../store/signalr/eventNames";
import CalloutStatuses from "../types/CalloutStatuses";
import DeletePopUp from "../../shared/DeletePopUp";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/rootReducer";
interface CalloutRequestedWidgetProps extends IAppPropsWithRoute {
  requestCalloutId: string;
}

const useStyles = makeStyles((theme) => ({
  container: {
    flex: "1",
    borderRadius: 0,
  },
  bodyContainer: {
    height: "100%",
    margin: "auto",
    width: "94%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "center",
    textAlign: "center",
  },
  body: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginTop: 56,
    width: "100%",

    "@media (max-width: 600px)": {
      marginTop: 24,
      width: 310,
    },
  },
  stepperItem: {
    marginTop: 42,

    "@media (max-width: 1024px)": {
      marginTop: 5,
      marginBottom: 30,
    },
  },
  bottomWrapper: {
    minHeight: 250,
    backgroundColor: "#001234",

    "@media (max-width: 600px)": {
      minHeight: 220,
    },

    "@media (max-width: 1024px)": {
      minHeight: 250,
    },
  },
}));

const getBottomComponent = (activeStepId: number, ratingState: number, setRatingState: any, goToRequestCallout: any, requestCalloutId: string, setETA: any) => {
  if (activeStepId <= CalloutStatuses.dispatchedToTeam) return <Dispatched />;

  switch (activeStepId) {
    case CalloutStatuses.teamAcknowledged:
      return <Acknowledged />;
    case CalloutStatuses.enRouteToDestination:
    case CalloutStatuses.teamOnSite:
      return <TeamOnTheirWay setETA={setETA} requestCalloutId={requestCalloutId} />;
    default:
      return <Feedback ratingState={ratingState} setRatingState={setRatingState} goToRequestCallout={goToRequestCallout} />;
  }
};

const isCallouFinished = (activeStepId: number) => activeStepId > CalloutStatuses.teamOnSite;

const componentTaskId = "calloutRequestedWidget";
const CalloutRequestedWidget: React.FC<CalloutRequestedWidgetProps> = ({ requestCalloutId, history }) => {
  const classes = useStyles();
  const [errorMessage, setErrorMessage] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [activeStepId, setActiveStepId] = React.useState(CalloutStatuses.dispatchedToTeam);
  const [calloutType, setCalloutType] = React.useState(RequestCalloutTypes.overloadedCallout);
  const [steps, setSteps] = React.useState(createCalloutSteps());
  const [ratingState, setRatingState] = React.useState(-1);
  const [eta, setETA] = React.useState("");
  const [confirmationDialog, setConfirmationDialog] = React.useState(false);
  const [wardName, setWardName] = React.useState("");
  const [locationName, setLocatiodName] = React.useState("");

  const goToRequestCallout = () => history.push(`/requestcallout`);
  const { authStore } = useSelector((state: RootState) => state);
  const getRequestCalloutDetails = async (): Promise<void> => {
    setErrorMessage("");
    setLoading(true);
    try {
      const res = await api.getRequestCalloutDetails(`requestCalloutId=${requestCalloutId}`);
      setLoading(false);
      const calloutModel: IRequestCalloutDTO = res?.model;
      if (calloutModel?.status && calloutModel.type) {
        setActiveStepId(adjustStatusToActiveStepId(calloutModel.status));
        setCalloutType(calloutModel.type);
        setSteps(prepareSteps(calloutModel.requestCalloutEvents));
      } else {
        throw "Something wrong";
      }
    } catch (error) {
      setErrorMessage("Error while getting callout details, try to refresh the page");
      setLoading(false);
    }
  };

  const getCalloutInitialData = async (): Promise<void> => {
    const res = await api.getCalloutLastRequestedDates("", {});
    const resModel = res?.data?.model;
    if (resModel) {
      if(localStorage.getItem("Reqcallout:LocationName") !== null && localStorage.getItem("Reqcallout:WardName") !== null && authStore.data?.IsCustomer)
      {
        var locname = localStorage.getItem("Reqcallout:LocationName");
        var wname = localStorage.getItem("Reqcallout:WardName");      
        setWardName(wname??"");
        setLocatiodName(locname??"");  
      }
      else
      {
        if(localStorage.getItem("Reqcallout:WardName") !== null){
          var wname = localStorage.getItem("Reqcallout:WardName");
          setWardName(wname??"");
        }
        else{
          setWardName(resModel.wardName);
        }     
        setLocatiodName(resModel.locationName);
      }
    }
  };

  React.useEffect(() => {
    getCalloutInitialData();
  }, []);

  React.useEffect(() => {
    getRequestCalloutDetails();
  }, [requestCalloutId]);

  const whenCalloutEventDo = () => {
    getRequestCalloutDetails();
  };

  React.useEffect(() => {
    addTaskForEvent(eventNames.refreshCallouts, componentTaskId, whenCalloutEventDo);
    return () => {
      removeTaskForEvent(eventNames.refreshCallouts, componentTaskId);
    };
  }, [whenCalloutEventDo]);

  const cancelCallout = async () => {
    setErrorMessage("");
    setLoading(true);
    try {
      const res = await api.cancelRequestCallout("", { requestCalloutId });
      setLoading(false);
      if (res.hasError === false) {
        history.replace(`/requestcallout`);
      } else {
        //throw res.message;
        setErrorMessage(res.message);
        setLoading(false);
      }
    } catch (error) {
      setErrorMessage(`Error while cancelling callout`);
      setLoading(false);
    }
  };

  const addFeedackOnDone = async () => {
    setErrorMessage("");
    setLoading(true);
    try {
      const res = await api.addCalloutRequestFeedack("", { requestCalloutId, score: ratingState });
      setLoading(false);
      if (res.hasError === false) {
        history.replace(`/requestcallout`);
      } else {
        throw res.message;
      }
    } catch (error) {
      setErrorMessage(`Error while adding feedback`);
      setLoading(false);
    }
  };

  const changeRequestCalloutType = async () => {
    setErrorMessage("");
    setLoading(true);
    try {
      const type = calloutType === RequestCalloutTypes.incidentCallout ? RequestCalloutTypes.overloadedCallout : RequestCalloutTypes.incidentCallout;
      const res = await api.changeRequestCalloutType("", { requestCalloutId, type });
      setLoading(false);
      if (res.hasError === false) {
        setCalloutType(type);
      } else {
        throw res.message;
      }
    } catch (error) {
      setErrorMessage(`Error while changing callout`);
      setLoading(false);
    }
  };

  return (
    <>
      <MuiThemeProvider theme={themeDark}>
        <Paper className={classes.container}>
          <div className={classes.bodyContainer}>
            <div className={classes.body}>
              <Grid container>
                <Grid item xs={12}>
                  <CalloutRequestedTop
                    isItIncident={calloutType === RequestCalloutTypes.incidentCallout}
                    cancelCallout={() => setConfirmationDialog(true)}
                    changeRequestCalloutType={changeRequestCalloutType}
                    isCallouFinished={isCallouFinished(activeStepId)}
                    onDone={addFeedackOnDone}
                    wardName={wardName}
                    locationName={locationName}
                  />
                </Grid>
                {errorMessage && <ErrorMessage errorMessage={errorMessage} />}
                <Grid item xs={12} className={classes.stepperItem}>
                  <CalloutRequestedStepper steps={steps} activeStepId={activeStepId} isCalloutFinished={isCallouFinished(activeStepId)} eta={eta} />
                </Grid>
              </Grid>
            </div>
          </div>
        </Paper>
        <div className={classes.bottomWrapper}>
          {getBottomComponent(activeStepId, ratingState, setRatingState, goToRequestCallout, requestCalloutId, setETA)}
        </div>
      </MuiThemeProvider>
      <DeletePopUp
        isDialogOpen={confirmationDialog}
        onCancel={() => setConfirmationDialog(false)}
        onDo={cancelCallout}
        title="Cancel callout"
        recordName=""
        onlyYesButton={true}
        yesButtonText="Cancel callout"
        text="Are you sure you would like to cancel this callout?"
      />
    </>
  );
};

export default withRouter(CalloutRequestedWidget);
